import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';

const COOKIE_NAME = 'cookie_preferences';
const COOKIE_EXPIRY = 365; // days

// Default cookie preferences
const DEFAULT_PREFERENCES = {
  essential: true, // Always true, can't be disabled
  analytics: false,
  preferences: false,
};

// Get saved preferences from cookie
const getSavedPreferences = () => {
  try {
    const saved = Cookies.get(COOKIE_NAME);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error parsing cookie preferences:', e);
  }
  return null;
};

// Save preferences to cookie
const savePreferences = (prefs) => {
  Cookies.set(COOKIE_NAME, JSON.stringify(prefs), { expires: COOKIE_EXPIRY });
};

// Check if user has made a choice
export const hasConsentChoice = () => {
  return getSavedPreferences() !== null;
};

// Get current preferences (for checking if analytics is allowed)
export const getConsentPreferences = () => {
  return getSavedPreferences() || DEFAULT_PREFERENCES;
};

// Check if specific cookie type is allowed
export const isCookieAllowed = (type) => {
  const prefs = getConsentPreferences();
  return prefs[type] === true;
};

/**
 * Professional Cookie Banner Component
 * 
 * Usage contexts:
 * - context="public" - For public pages (Landing, About, Pricing, etc.) - uses i18n
 * - context="menu" - For public menu pages - uses language prop
 * - context="admin" - For tenant admin pages - uses i18n
 * 
 * @param {string} context - The context where banner is displayed
 * @param {string} language - Language for menu context (en/tr)
 * @param {function} t - Translation function (from useTranslation or usePublicTranslation)
 * @param {function} onPreferencesChange - Callback when preferences change
 */
export default function CookieBanner({ 
  context = 'public', 
  language = 'en',
  t: externalT,
  onPreferencesChange 
}) {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  // Internal translation function with fallbacks
  const t = useCallback((key) => {
    if (externalT) {
      const result = externalT(key);
      // If translation returns the key itself, use fallback
      if (result !== key) return result;
    }
    // Fallback translations
    const fallbacks = {
      en: {
        'cookieBanner.title': 'We value your privacy',
        'cookieBanner.description': 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. You can choose which cookies you allow.',
        'cookieBanner.acceptAll': 'Accept All',
        'cookieBanner.rejectAll': 'Reject All',
        'cookieBanner.managePreferences': 'Manage Preferences',
        'cookieBanner.savePreferences': 'Save Preferences',
        'cookieBanner.essential': 'Essential',
        'cookieBanner.essentialDesc': 'Required for the website to function properly. Cannot be disabled.',
        'cookieBanner.analytics': 'Analytics',
        'cookieBanner.analyticsDesc': 'Help us understand how visitors interact with our website.',
        'cookieBanner.preferences': 'Preferences',
        'cookieBanner.preferencesDesc': 'Remember your settings and personalization choices.',
        'cookieBanner.learnMore': 'Learn more in our',
        'cookieBanner.cookiePolicy': 'Cookie Policy',
        'cookieBanner.required': 'Required',
      },
      tr: {
        'cookieBanner.title': 'Gizliliğinize değer veriyoruz',
        'cookieBanner.description': 'Tarama deneyiminizi geliştirmek, site trafiğini analiz etmek ve içeriği kişiselleştirmek için çerezler kullanıyoruz. Hangi çerezlere izin vereceğinizi seçebilirsiniz.',
        'cookieBanner.acceptAll': 'Tümünü Kabul Et',
        'cookieBanner.rejectAll': 'Tümünü Reddet',
        'cookieBanner.managePreferences': 'Tercihleri Yönet',
        'cookieBanner.savePreferences': 'Tercihleri Kaydet',
        'cookieBanner.essential': 'Zorunlu',
        'cookieBanner.essentialDesc': 'Web sitesinin düzgün çalışması için gereklidir. Devre dışı bırakılamaz.',
        'cookieBanner.analytics': 'Analitik',
        'cookieBanner.analyticsDesc': 'Ziyaretçilerin web sitemizle nasıl etkileşime girdiğini anlamamıza yardımcı olur.',
        'cookieBanner.preferences': 'Tercihler',
        'cookieBanner.preferencesDesc': 'Ayarlarınızı ve kişiselleştirme tercihlerinizi hatırlar.',
        'cookieBanner.learnMore': 'Daha fazla bilgi için',
        'cookieBanner.cookiePolicy': 'Çerez Politikası',
        'cookieBanner.required': 'Zorunlu',
      }
    };
    const lang = language === 'tr' ? 'tr' : 'en';
    return fallbacks[lang][key] || key;
  }, [externalT, language]);

  useEffect(() => {
    const saved = getSavedPreferences();
    if (saved) {
      setPreferences(saved);
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = { essential: true, analytics: true, preferences: true };
    savePreferences(allAccepted);
    setPreferences(allAccepted);
    setVisible(false);
    onPreferencesChange?.(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyEssential = { essential: true, analytics: false, preferences: false };
    savePreferences(onlyEssential);
    setPreferences(onlyEssential);
    setVisible(false);
    onPreferencesChange?.(onlyEssential);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setVisible(false);
    setShowDetails(false);
    onPreferencesChange?.(preferences);
  };

  const togglePreference = (type) => {
    if (type === 'essential') return; // Can't toggle essential
    setPreferences(prev => ({ ...prev, [type]: !prev[type] }));
  };

  if (!visible) return null;

  // Determine cookie policy link based on context
  const cookiePolicyLink = context === 'menu' ? '/cookies' : '/cookies';

  return (
    <div 
      role="dialog" 
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-0 bottom-0 z-[9999] p-4 md:p-6"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Main Banner */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Cookie Icon */}
            <div className="hidden sm:flex items-center justify-center w-12 h-12 bg-purple-100 rounded-xl flex-shrink-0">
              <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <div className="flex-1 min-w-0">
              <h2 id="cookie-banner-title" className="text-lg font-semibold text-gray-900 mb-2">
                {t('cookieBanner.title')}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t('cookieBanner.description')}{' '}
                <Link 
                  to={cookiePolicyLink} 
                  className="text-purple-600 hover:text-purple-700 underline font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('cookieBanner.cookiePolicy')}
                </Link>
              </p>

              {/* Cookie Categories (when expanded) */}
              {showDetails && (
                <div className="space-y-3 mb-4 p-4 bg-gray-50 rounded-xl">
                  {/* Essential Cookies */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{t('cookieBanner.essential')}</span>
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                          {t('cookieBanner.required')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{t('cookieBanner.essentialDesc')}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-11 h-6 bg-purple-600 rounded-full relative cursor-not-allowed opacity-60">
                        <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow" />
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex-1 min-w-0 pr-4">
                      <span className="font-medium text-gray-900">{t('cookieBanner.analytics')}</span>
                      <p className="text-xs text-gray-500 mt-1">{t('cookieBanner.analyticsDesc')}</p>
                    </div>
                    <button 
                      onClick={() => togglePreference('analytics')}
                      className={`flex-shrink-0 w-11 h-6 rounded-full relative transition-colors ${
                        preferences.analytics ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                      role="switch"
                      aria-checked={preferences.analytics}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${
                        preferences.analytics ? 'right-1' : 'left-1'
                      }`} />
                    </button>
                  </div>

                  {/* Preference Cookies */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex-1 min-w-0 pr-4">
                      <span className="font-medium text-gray-900">{t('cookieBanner.preferences')}</span>
                      <p className="text-xs text-gray-500 mt-1">{t('cookieBanner.preferencesDesc')}</p>
                    </div>
                    <button 
                      onClick={() => togglePreference('preferences')}
                      className={`flex-shrink-0 w-11 h-6 rounded-full relative transition-colors ${
                        preferences.preferences ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                      role="switch"
                      aria-checked={preferences.preferences}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${
                        preferences.preferences ? 'right-1' : 'left-1'
                      }`} />
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {showDetails ? (
                  <>
                    <button
                      onClick={handleSavePreferences}
                      className="flex-1 sm:flex-none px-5 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      {t('cookieBanner.savePreferences')}
                    </button>
                    <button
                      onClick={() => setShowDetails(false)}
                      className="flex-1 sm:flex-none px-5 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      ← Back
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleAcceptAll}
                      className="flex-1 sm:flex-none px-5 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      {t('cookieBanner.acceptAll')}
                    </button>
                    <button
                      onClick={handleRejectAll}
                      className="flex-1 sm:flex-none px-5 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t('cookieBanner.rejectAll')}
                    </button>
                    <button
                      onClick={() => setShowDetails(true)}
                      className="flex-1 sm:flex-none px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {t('cookieBanner.managePreferences')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Floating Cookie Settings Button - to reopen preferences
 */
export function CookieSettingsButton({ language = 'en', onClick }) {
  const label = language === 'tr' ? 'Çerez Ayarları' : 'Cookie Settings';
  
  return (
    <button
      onClick={onClick}
      className="fixed bottom-4 left-4 z-[9998] p-3 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors"
      title={label}
      aria-label={label}
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </button>
  );
}
